import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useContextDrawerStore } from "../store/contextDrawerStore";
import { fetchContextExplain, ContextCard, ContextExplainResponse } from "../api/contextExplainApi";
import { useAIContext } from "../store/aiContextStore";
import { useSlashStore } from "../store/slashStore";
import { useScopeRouterStore } from "../store/scopeRouterStore";
import { useOffsetRegistryStore } from "../store/offsetRegistryStore";
import { useNationalSyncStore } from "../store/nationalSyncStore";
import { useScopeExchangeStore } from "../store/scopeExchangeStore";
import { useESealStore } from "../store/eSealStore";
import { useCertificateNFTStore } from "../store/certificateNFTStore";
import { useEUFabricStore } from "../store/euFabricStore";
import { useEvidenceChainStore } from "../store/evidenceChainStore";
import { useTenderMeshStore } from "../store/tenderMeshStore";
import { useTribunalStore } from "../store/tribunalStore";
import { useESGPassportRegistryStore } from "../store/esgPassportRegistryStore";
import { useSubsidyEligibilityStore } from "../store/subsidyEligibilityStore";
import { useSubsidyAllocationStore } from "../store/subsidyAllocationStore";
import { useSovereignReconciliationStore } from "../store/sovereignReconciliationStore";
import { useBeneficialGraphStore } from "../store/beneficialGraphStore";
import { useJudicialTriggerStore } from "../store/judicialTriggerStore";
import { useSatelliteOverlayStore } from "../store/satelliteOverlayStore";
import { useAntiFraudStore } from "../store/antiFraudStore";
import { useMiniESGStore } from "../store/miniESGStore";

/**
 * Dynamic Context Drawer (v1)
 * 
 * Automatically appears when sovereign/compliance/trading panels are open.
 * Shows AI-generated contextual information cards with KPIs, EU taxonomy
 * references, remediation suggestions, and compliance impact analysis.
 * 
 * Features:
 * - Auto-appears on relevant panels
 * - Fetches context via API
 * - Internationalized (i18n)
 * - Works with Bottom AI Panel context
 * - Auto-updates on input changes
 * - Dark black/gold theme with neon highlights
 */
export default function ContextDrawer() {
  const { t } = useTranslation();
  const { enabled, open, activeRoute, inputSnapshot, lastFetch, toggle, setActiveRoute, updateInputSnapshot, setLastFetch } = useContextDrawerStore();
  const activePanel = useAIContext((state) => state.activePanel);
  const inputs = useAIContext((state) => state.inputs);
  const [data, setData] = useState<ContextExplainResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Detect if current route should show drawer (sovereign/compliance/trading panels)
  const shouldShowDrawer = useMemo(() => {
    if (!enabled) return false;
    
      const relevantRoutes = [
      "ets", "etsbridge",
      "taxonomy", "taxonomyoracle",
      "slasher", "complianceslasher",
      "futures", "futuresdesk",
      "tenderscore", "tenderscoring",
      "kms", "kmsmanager",
      "custody", "chainofcustody",
      "dpia",
      "eseal",
      "natsync", "nationalsync",
      "regexport",
      "portfolio", "portfoliobot",
      "vol", "volatility",
      "auction", "auctionhouse",
      "scoperouter", "scoperoute",
      "smartoffset", "offset",
      "scopeexchange", "scope-exchange",
      "eseal", "peppol",
      "certificatenft", "certificate-nft",
      "eufabric", "eu-fabric",
      "evidencechain", "evidence-chain",
      "tendermesh", "tender-mesh",
      "tribunal", "procurement-tribunal",
      "passport", "passport-registry", "esgpassport",
      "subsidy", "subsidy-eligibility",
      "beneficial", "beneficial-graph", "ownership-graph",
      "judicial", "judicial-trigger",
      "satelliteoverlay", "satellite-overlay",
      "antifraud", "anti-fraud", "anti-fraud-interoperability",
    ];
    
    // Try to detect route from activePanel or use activeRoute
    const currentRoute = activeRoute || activePanel?.toLowerCase().replace(/\s+/g, "") || "";
    return relevantRoutes.some((route) => currentRoute.includes(route));
  }, [enabled, activeRoute, activePanel]);

  // Auto-update route based on active panel
  useEffect(() => {
    if (activePanel) {
      const routeMap: Record<string, string> = {
        "ETS Bridge": "etsbridge",
        "Taxonomy Oracle": "taxonomyoracle",
        "Compliance Slasher": "complianceslasher",
        "Futures": "futuresdesk",
        "Tender Scoring": "tenderscoring",
        "KMS": "kmsmanager",
        "Chain of Custody": "chainofcustody",
        "DPIA": "dpia",
        "eSeal & PEPPOL": "eseal",
        "National Sync": "nationalsync",
        "Regulator Export": "regexport",
        "Portfolio Bot": "portfoliobot",
        "Volatility": "volatility",
        "Auction House": "auctionhouse",
        "Scope Router": "scoperouter",
        "Smart Offset": "smartoffset",
        "Scope Exchange": "scopeexchange",
        "eSeal / PEPPOL": "eseal",
        "Certificate NFT": "certificatenft",
        "EU Scope Exchange Fabric": "eufabric",
        "EU Evidence Chain": "evidencechain",
        "Tender Integrity Mesh": "tendermesh",
        "Procurement Tribunal": "tribunal",
        "ESG Passport Registry": "passport",
        "Subsidy Eligibility Engine": "subsidy",
        "Beneficial Ownership Graph": "beneficial",
        "Judicial Trigger Engine": "judicial",
        "Satellite Overlay Risk": "satelliteoverlay",
        "EU Anti-Fraud Interoperability Layer": "antifraud",
      };
      const route = routeMap[activePanel] || activePanel.toLowerCase().replace(/\s+/g, "");
      setActiveRoute(route);
    }
  }, [activePanel, setActiveRoute]);

  // Update input snapshot when inputs change - use ref to track previous value
  const prevInputsRef = useRef<string>("");
  useEffect(() => {
    if (inputs && Object.keys(inputs).length > 0) {
      const inputsStr = JSON.stringify(inputs);
      if (inputsStr !== prevInputsRef.current) {
        prevInputsRef.current = inputsStr;
        updateInputSnapshot(inputs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, updateInputSnapshot]);

  // Fetch context data when route or inputs change - use ref to prevent infinite loops
  const prevSnapshotRef = useRef<string>("");
  useEffect(() => {
    if (!shouldShowDrawer || !activeRoute) return;

    const snapshotStr = JSON.stringify(inputSnapshot);
    if (snapshotStr === prevSnapshotRef.current) return;
    prevSnapshotRef.current = snapshotStr;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchContextExplain(activeRoute, inputSnapshot);
        setData(response);
        setLastFetch(new Date());
      } catch (err) {
        console.error("Failed to fetch context explain:", err);
        // Error is handled gracefully - drawer still shows store cards
      } finally {
        setLoading(false);
      }
    };

    // Debounce to avoid too frequent fetches
    const timeoutId = setTimeout(fetchData, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoute, JSON.stringify(inputSnapshot), shouldShowDrawer]);

  if (!shouldShowDrawer) {
    return null;
  }

  // Toggle button (always visible when drawer should be shown)
  if (!open) {
    return (
      <button
        onClick={toggle}
        className="fixed bottom-[240px] right-4 w-12 h-12 bg-evat-gold/20 border-2 border-evat-gold rounded-full flex items-center justify-center text-evat-gold hover:bg-evat-gold/30 hover:shadow-lg hover:shadow-evat-gold/50 transition-all z-[9999]"
        aria-label={t("contextDrawer.open") || "Open context drawer"}
        style={{ backdropFilter: "blur(8px)" }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M6 4l8 6-8 6V4z" />
        </svg>
      </button>
    );
  }

  return (
    <div
      className="fixed right-0 top-0 bottom-[220px] w-96 border-l border-evat-gold/40 bg-black z-[9998] overflow-y-auto"
      style={{ backdropFilter: "blur(8px)" }}
      aria-label={t("contextDrawer.ariaLabel") || "Context Drawer"}
    >
        {/* Header */}
        <div className="sticky top-0 border-b border-evat-gold/40 bg-black/95 p-4 flex items-center justify-between z-10">
          <h2 className="font-orbitron text-lg text-evat-gold">
            {t("contextDrawer.title") || "Context Insights"}
          </h2>
          <button
            onClick={toggle}
            className="transition-colors p-1 text-evat-gold hover:text-evat-neon"
            aria-label={t("contextDrawer.close") || "Close drawer"}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {loading && (
            <div className="text-center text-neutral-400 py-8">
              {t("contextDrawer.loading") || "Loading context..."}
            </div>
          )}

          {!loading && data && (
            <>
              {/* Compliance Warning */}
              {data.warning && (
                <div
                  className={`p-3 rounded border ${
                    data.complianceStatus === "critical"
                      ? "bg-red-900/20 border-red-500 text-red-300"
                      : data.complianceStatus === "gap"
                      ? "bg-yellow-900/20 border-yellow-500 text-yellow-300"
                      : "bg-blue-900/20 border-blue-500 text-blue-300"
                  }`}
                >
                  <div className="text-sm font-semibold mb-1">
                    {data.complianceStatus === "critical"
                      ? t("contextDrawer.critical") || "⚠️ Critical"
                      : data.complianceStatus === "gap"
                      ? t("contextDrawer.gap") || "⚠️ Gap"
                      : t("contextDrawer.info") || "ℹ️ Info"}
                  </div>
                  <div className="text-xs">{data.warning}</div>
                </div>
              )}

              {/* Context Cards */}
              {data.cards.map((card, idx) => (
                <ContextCardComponent key={idx} card={card} index={idx} />
              ))}

              {/* Module-specific Store Data Cards */}
              <ModuleStoreCards activeRoute={activeRoute} />

              {/* Footer Info */}
              {lastFetch && (
                <div className="text-xs pt-4 border-t text-neutral-500 border-neutral-800">
                  {t("contextDrawer.lastUpdate") || "Last updated"}:{" "}
                  {lastFetch.toLocaleTimeString()}
                </div>
              )}
            </>
          )}

          {!loading && !data && (
            <div className="text-center text-neutral-500 py-8">
              {t("contextDrawer.noData") || "No context data available"}
            </div>
          )}
        </div>
      </div>
  );
}

/**
 * Individual context card component
 */
function ContextCardComponent({ card, index }: { card: ContextCard; index: number }) {
  const { t } = useTranslation();

  return (
    <div
      className="border border-evat-gold/30 bg-black/50 hover:border-evat-gold/60 rounded-lg p-4 hover:transition-colors"
    >
      {/* Primary KPI */}
      <div className="mb-3">
        <div className="text-xs text-neutral-400 mb-1">
          {t("contextDrawer.primaryKpi") || "Primary KPI"}
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-lg font-orbitron text-evat-gold">
            {card.primaryKpiValue}
          </div>
          <div className="text-sm text-neutral-300">{card.primaryKpi}</div>
        </div>
      </div>

      {/* Secondary KPI */}
      <div className="mb-3">
        <div className="text-xs text-neutral-400 mb-1">
          {t("contextDrawer.secondaryKpi") || "Secondary KPI"}
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-base font-orbitron text-evat-neon">
            {card.secondaryKpiValue}
          </div>
          <div className="text-sm text-neutral-300">{card.secondaryKpi}</div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
        {/* CO2e Delta */}
        <div className="border border-neutral-700 rounded p-2">
          <div className="text-neutral-400 mb-1">
            {t("contextDrawer.co2eDelta") || "CO₂e Δ"}
          </div>
          <div
            className={`font-semibold ${
              card.co2eDelta < 0 ? "text-green-400" : card.co2eDelta > 0 ? "text-red-400" : "text-neutral-400"
            }`}
          >
            {card.co2eDelta > 0 ? "+" : ""}
            {card.co2eDelta.toLocaleString()} kg
          </div>
        </div>

        {/* Cost Savings */}
        <div className="border border-neutral-700 rounded p-2">
          <div className="text-neutral-400 mb-1">
            {t("contextDrawer.costSavings") || "Cost Savings"}
          </div>
          <div className="font-semibold text-evat-gold">
            €{card.costSavingsEur.toLocaleString()}
          </div>
        </div>

        {/* Time */}
        <div className="border border-neutral-700 rounded p-2">
          <div className="text-neutral-400 mb-1">
            {t("contextDrawer.timeDays") || "Time"}
          </div>
          <div className="font-semibold text-neutral-300">
            {card.timeDays} {t("contextDrawer.days") || "days"}
          </div>
        </div>

        {/* Difficulty */}
        <div className="border border-neutral-700 rounded p-2">
          <div className="text-neutral-400 mb-1">
            {t("contextDrawer.difficulty") || "Difficulty"}
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`w-2 h-2 rounded-full ${
                  level <= card.difficulty
                    ? "bg-evat-neon"
                    : "bg-neutral-700"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* EU Article Reference */}
      <div className="mb-3 p-2 bg-neutral-900/50 rounded border border-evat-gold/20">
        <div className="text-xs text-neutral-400 mb-1">
          {t("contextDrawer.euArticle") || "EU Article Reference"}
        </div>
        <div className="text-sm text-evat-gold font-mono">
          {card.euArticleRef}
        </div>
      </div>

      {/* Slashing Impact */}
      {card.slashingImpact && card.slashingImpact > 0 && (
        <div className="mb-3 p-2 bg-red-900/20 rounded border border-red-500/40">
          <div className="text-xs text-red-300 mb-1">
            {t("contextDrawer.slashingImpact") || "ESG Slashing Impact"}
          </div>
          <div className="text-sm font-semibold text-red-400">
            -{card.slashingImpact}%
          </div>
        </div>
      )}

      {/* Remediation */}
      <div className="border-t border-neutral-700 pt-3">
        <div className="text-xs text-neutral-400 mb-1">
          {t("contextDrawer.remediation") || "Recommended Remediation"}
        </div>
        <div className="text-sm text-neutral-200">{card.remediation}</div>
      </div>
    </div>
  );
}

/**
 * Module-specific store data cards
 */
function ModuleStoreCards({ activeRoute }: { activeRoute: string | null }) {
  const { t } = useTranslation();
  const activePanel = useAIContext((state) => state.activePanel);
  
  // Get stores - hooks must be called unconditionally
  const slashStore = useSlashStore();
  const antiFraudStore = useAntiFraudStore();
  const scopeRouterStore = useScopeRouterStore();
  const offsetRegistryStore = useOffsetRegistryStore();
  const nationalSyncStore = useNationalSyncStore();
  const scopeExchangeStore = useScopeExchangeStore();
  const eSealStore = useESealStore();
  const certificateNFTStore = useCertificateNFTStore();
  const euFabricStore = useEUFabricStore();
  const evidenceChainStore = useEvidenceChainStore();
  const tenderMeshStore = useTenderMeshStore();
  const tribunalStore = useTribunalStore();
  const esgPassportRegistryStore = useESGPassportRegistryStore();
  const subsidyEligibilityStore = useSubsidyEligibilityStore();
  const subsidyAllocationStore = useSubsidyAllocationStore();
  const sovereignReconciliationStore = useSovereignReconciliationStore();
  const beneficialGraphStore = useBeneficialGraphStore();
  const judicialTriggerStore = useJudicialTriggerStore();
  const satelliteOverlayStore = useSatelliteOverlayStore();
  const miniESGStore = useMiniESGStore();

  // Auto-refresh ContextDrawer when store values change
  useEffect(() => {
    try {
      useContextDrawerStore.getState().setLastFetch(new Date());
    } catch (error) {
      console.error("Error updating lastFetch:", error);
    }
  }, [
    slashStore.gapScore,
    slashStore.severity,
    scopeRouterStore.scopeCategory,
    scopeRouterStore.articleRef,
    offsetRegistryStore.offsetBalance,
    nationalSyncStore.ackStatus,
    scopeExchangeStore.outgoingPayloadKB,
    scopeExchangeStore.integrityScore,
    scopeExchangeStore.reconciliationDelayDays,
    eSealStore.sealConfidence,
    eSealStore.anchorLedgerHeight,
    eSealStore.sealSignature,
    certificateNFTStore.esgScore,
    certificateNFTStore.certificateLevel,
    certificateNFTStore.revocationRisk,
    euFabricStore.replicationLagMS,
    euFabricStore.reconciliationErrors,
    euFabricStore.fabricHealthScore,
    euFabricStore.escalationLevel,
    euFabricStore.anomalyFlags,
    evidenceChainStore.chainLength,
    evidenceChainStore.chainIntegrityScore,
    evidenceChainStore.admissibilityLevel,
    evidenceChainStore.chainJitterMS,
    evidenceChainStore.chainDriftMinutes,
    tenderMeshStore.procurementRiskScore,
    tenderMeshStore.transparencyScore,
    tenderMeshStore.conflictOfInterestFlags,
    tenderMeshStore.corruptionSignals,
    tenderMeshStore.meshLatencyMS,
    tribunalStore.tribunalDecision,
    tribunalStore.remediationDemandEUR,
    esgPassportRegistryStore.registryHealth,
    esgPassportRegistryStore.revokedCount,
    esgPassportRegistryStore.passports?.length,
    subsidyEligibilityStore.subsidyEligibilityScore,
    subsidyEligibilityStore.grantReductionPercent,
    subsidyEligibilityStore.riskOfMisuseFlag,
    subsidyEligibilityStore.escalationPath,
    beneficialGraphStore.conflictScore,
    beneficialGraphStore.oligarchClusterScore,
    beneficialGraphStore.integrityRisk,
    beneficialGraphStore.nepotismFlags,
    beneficialGraphStore.tenderCollisionCount,
    beneficialGraphStore.graphDepth,
    miniESGStore.score.total,
    miniESGStore.score.environment,
    miniESGStore.score.social,
    miniESGStore.score.governance,
  ]);

  const cards: (JSX.Element | null)[] = [];

  try {
  // Compliance Slasher cards
  if (activeRoute?.includes("slasher") || activeRoute?.includes("complianceslasher")) {
    try {
      cards.push(
        <StoreCard key="slasher-penalty" title={t("slasher.enforcementPenalty") || "Penalty"} value={slashStore.getEnforcementPenalty().toFixed(2)} color="red" description={t("slasher.enforcementPenaltyDesc") || "Financial penalty amount (EUR) for non-compliance. Higher values indicate more severe violations."} />,
        <StoreCard key="slasher-passport" title={t("slasher.passportDowngrade") || "Passport ↓"} value={`-${slashStore.getPassportDowngrade()}`} color="yellow" description={t("slasher.passportDowngradeDesc") || "ESG passport level downgrade points. Negative value indicates reduction in passport level."} />,
        <StoreCard key="slasher-co2tax" title={t("slasher.co2TaxAdjustment") || "CO₂ Tax Adj."} value={slashStore.getCo2TaxAdjustment().toFixed(2)} color="orange" description={t("slasher.co2TaxAdjustmentDesc") || "CO₂ tax adjustment amount (EUR). Positive values indicate additional tax burden."} />,
        <StoreCard key="slasher-friction" title={t("slasher.frictionIndex") || "Friction Index"} value={slashStore.getFrictionIndex().toFixed(3)} color="purple" description={t("slasher.frictionIndexDesc") || "Overall friction index (0-1). Higher values indicate more regulatory friction and compliance burden."} />,
        <StoreCard key="slasher-subsidy" title={t("slasher.subsidyEligibility") || "Subsidy Eligible"} value={slashStore.subsidyEligible ? "Yes" : "No"} color={slashStore.subsidyEligible ? "green" : "red"} description={t("slasher.subsidyEligibilityDesc") || "Whether the entity remains eligible for subsidies after compliance slashing. No = subsidy access revoked."} />,
        <StoreCard key="slasher-remediation-days" title={t("slasher.remediationETA") || "Remediation ETA"} value={`${slashStore.remediationDays} days`} color="blue" description={t("slasher.remediationETADesc") || "Estimated time to remediation in days. Lower values indicate faster recovery."} />,
        <StoreCard key="slasher-remediation-cost" title={t("slasher.remediationCost") || "Remediation Cost"} value={`€${slashStore.remediationCostEUR.toLocaleString()}`} color="gold" description={t("slasher.remediationCostDesc") || "Total cost (EUR) required to remediate compliance issues and restore eligibility."} />
      );
    } catch (error) {
      console.error("Error rendering Compliance Slasher cards:", error);
    }
  }

  // Scope Router cards
  if (activeRoute?.includes("scoperouter") || activeRoute?.includes("scoperoute")) {
    try {
      const routingPath = scopeRouterStore.getRoutingPath();
      const ledgerTarget = scopeRouterStore.getLedgerTarget();
      const crossBorderBonus = scopeRouterStore.getCrossBorderBonus();
      const articleMismatch = scopeRouterStore.getArticleMismatch();

      cards.push(
        <StoreCard key="scope-routing" title={t("scopeRouter.routingPath") || "Routing Path"} value={routingPath?.join(" → ") || ""} color="neon" multiline description={t("scopeRouter.routingPathDesc") || "Optimal routing path for scope transfer across borders. Shows the sequence of intermediate nodes."} />,
        <StoreCard key="scope-ledger" title={t("scopeRouter.ledgerTarget") || "Ledger Target"} value={ledgerTarget || ""} color="gold" description={t("scopeRouter.ledgerTargetDesc") || "Target ledger system where the scope will be recorded. Can be national, EU, or private ledger."} />,
        <StoreCard key="scope-bonus" title={t("scopeRouter.crossBorderBonus") || "Cross-Border Bonus"} value={`+${crossBorderBonus?.toFixed(2) || "0.00"}`} color="green" description={t("scopeRouter.crossBorderBonusDesc") || "Bonus points added for cross-border scope transfers. Encourages international ESG cooperation."} />,
        articleMismatch ? (
          <div key="scope-mismatch" className="border border-red-500/40 rounded p-3 bg-red-900/20 text-red-300 text-xs">
            ⚠️ {t("scopeRouter.articleMismatch") || "Article mismatch detected!"}
          </div>
        ) : null
      );
    } catch (error) {
      console.error("Error rendering Scope Router cards:", error);
    }
  }

  // Smart Offset Registry cards
  if (activeRoute?.includes("offset") || activeRoute?.includes("smartoffset")) {
    try {
      const fraudRisk = offsetRegistryStore.getFraudRisk();
      const localBoost = offsetRegistryStore.getLocalBoost();
      const agingBuckets = offsetRegistryStore.getAgingBuckets();

      cards.push(
        <StoreCard key="offset-balance" title={t("offsetRegistry.offsetBalance") || "Offset Balance"} value={(offsetRegistryStore.offsetBalance || 0).toLocaleString()} color="gold" description={t("offsetRegistry.offsetBalanceDesc") || "Total offset credits available in the registry. Higher values indicate more available offsets."} />,
        <StoreCard key="offset-localboost" title={t("offsetRegistry.localBoost") || "Local Boost"} value={`+${(localBoost || 0).toFixed(2)}%`} color="green" description={t("offsetRegistry.localBoostDesc") || "Percentage bonus for locally sourced offsets. Encourages regional ESG initiatives."} />,
        <StoreCard key="offset-fraudrisk" title={t("offsetRegistry.fraudRisk") || "Fraud Risk"} value={`${(fraudRisk || 0).toFixed(2)}%`} color="red" description={t("offsetRegistry.fraudRiskDesc") || "Risk percentage for offset fraud. Higher values indicate greater risk of fraudulent offsets."} />,
        <StoreCard key="offset-imported" title={t("offsetRegistry.importedOffsets") || "Imported Offsets"} value={(offsetRegistryStore.importedOffsets || 0).toString()} color="yellow" description={t("offsetRegistry.importedOffsetsDesc") || "Number of offsets imported from external registries. May have different validation requirements."} />,
        <div key="offset-aging" className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-2">{t("offsetRegistry.agingBuckets") || "Aging Buckets"}</div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            {agingBuckets && Object.entries(agingBuckets).map(([bucket, count]) => (
              <div key={bucket} className="text-center">
                <div className="text-evat-neon">{count}</div>
                <div className="text-neutral-500">{bucket}</div>
              </div>
            ))}
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error rendering Smart Offset Registry cards:", error);
    }
  }

  // National Sync cards
  if (activeRoute?.includes("nationalsync") || activeRoute?.includes("natsync")) {
    try {
      const retryPolicy = nationalSyncStore.getRetryPolicy();

      cards.push(
        <StoreCard key="sync-ledgerheight" title={t("nationalSync.ledgerHeight") || "Ledger Height"} value={nationalSyncStore.ledgerHeight.toLocaleString()} color="gold" description={t("nationalSync.ledgerHeightDesc") || "Current blockchain ledger height. Indicates the number of blocks in the chain."} />,
        <StoreCard key="sync-envelopesize" title={t("nationalSync.envelopeSize") || "Envelope Size"} value={`${nationalSyncStore.envelopeSizeKB} KB`} color="neon" description={t("nationalSync.envelopeSizeDesc") || "Size of the synchronization envelope in kilobytes. Larger envelopes may take longer to sync."} />,
        <StoreCard key="sync-ackstatus" title={t("nationalSync.ackStatus") || "Ack Status"} value={nationalSyncStore.ackStatus.toUpperCase()} color={nationalSyncStore.ackStatus === "accepted" ? "green" : nationalSyncStore.ackStatus === "rejected" ? "red" : "yellow"} description={t("nationalSync.ackStatusDesc") || "Acknowledgment status from the national ledger. Accepted = sync successful, Rejected = sync failed, Pending = waiting for response."} />,
        <StoreCard key="sync-retries" title={t("nationalSync.retries") || "Retries"} value={`${nationalSyncStore.retries} / ${retryPolicy.maxRetries}`} color="orange" description={t("nationalSync.retriesDesc") || "Number of retry attempts vs maximum allowed. Higher retry counts indicate sync issues."} />,
        <StoreCard key="sync-retrypolicy" title={t("nationalSync.retryPolicy") || "Retry Policy"} value={`${retryPolicy.nextRetryMs}ms`} color="blue" description={t("nationalSync.retryPolicyDesc") || "Time delay in milliseconds before the next retry attempt. Exponential backoff is typically used."} />
      );
    } catch (error) {
      console.error("Error rendering National Sync cards:", error);
    }
  }

  // Scope Exchange cards
  if (activeRoute?.includes("scopeexchange") || activeRoute?.includes("scope-exchange")) {
    const riskPropagation = scopeExchangeStore.getRiskPropagation();
    const lossOfIntegrity = scopeExchangeStore.getLossOfIntegrity();
    const exchangeHealth = scopeExchangeStore.getExchangeHealth();

    cards.push(
      <StoreCard key="scopeex-outgoing" title={t("scopeExchange.outgoingPayloadKB") || "Outgoing Payload"} value={`${scopeExchangeStore.outgoingPayloadKB} KB`} color="neon" description={t("scopeExchange.outgoingPayloadKBDesc") || "Size of data sent to counterpart in kilobytes. Larger payloads may indicate complex scope transfers."} />,
      <StoreCard key="scopeex-incoming" title={t("scopeExchange.incomingPayloadKB") || "Incoming Payload"} value={`${scopeExchangeStore.incomingPayloadKB} KB`} color="gold" description={t("scopeExchange.incomingPayloadKBDesc") || "Size of data received from counterpart in kilobytes. Monitors incoming scope transfers."} />,
      <StoreCard key="scopeex-integrity" title={t("scopeExchange.integrityScore") || "Integrity Score"} value={`${scopeExchangeStore.integrityScore}%`} color={scopeExchangeStore.integrityScore >= 80 ? "green" : scopeExchangeStore.integrityScore >= 50 ? "yellow" : "red"} description={t("scopeExchange.integrityScoreDesc") || "Data integrity score (0-100%). Higher values indicate better data consistency and validation."} />,
      <StoreCard key="scopeex-reconciliation" title={t("scopeExchange.reconciliationDelayDays") || "Reconciliation Delay"} value={`${scopeExchangeStore.reconciliationDelayDays} days`} color="orange" description={t("scopeExchange.reconciliationDelayDaysDesc") || "Number of days delay in reconciliation. Higher delays may indicate synchronization issues."} />,
      <StoreCard key="scopeex-riskprop" title={t("scopeExchange.riskPropagation") || "Risk Propagation"} value={riskPropagation.toFixed(2)} color="red" description={t("scopeExchange.riskPropagationDesc") || "Risk propagation factor. Higher values indicate greater risk spreading across the exchange network."} />,
      <StoreCard key="scopeex-lossofint" title={t("scopeExchange.lossOfIntegrity") || "Loss of Integrity"} value={lossOfIntegrity.toFixed(3)} color="orange" description={t("scopeExchange.lossOfIntegrityDesc") || "Loss of integrity metric (0-1). Higher values indicate more data integrity issues."} />,
      <StoreCard key="scopeex-health" title={t("scopeExchange.exchangeHealth") || "Exchange Health"} value={`${exchangeHealth.toFixed(1)}%`} color={exchangeHealth >= 80 ? "green" : exchangeHealth >= 50 ? "yellow" : "red"} description={t("scopeExchange.exchangeHealthDesc") || "Overall exchange health percentage (0-100%). Combines integrity, delays, and risk factors."} />,
      <StoreCard key="scopeex-category" title={t("scopeExchange.scopeCategory") || "Scope Category"} value={scopeExchangeStore.scopeCategory} color="blue" description={t("scopeExchange.scopeCategoryDesc") || "Scope category (e.g., SCOPE1, SCOPE2, SCOPE3). Determines accounting and reporting requirements."} />,
      <StoreCard key="scopeex-counterpart" title={t("scopeExchange.exchangeCounterpart") || "Counterpart"} value={scopeExchangeStore.exchangeCounterpart || "N/A"} color="neon" description={t("scopeExchange.exchangeCounterpartDesc") || "Counterpart entity in the scope exchange. Can be another company, registry, or national system."} />
    );
  }

  // eSeal cards
  if (activeRoute?.includes("eseal") || activeRoute?.includes("peppol")) {
    const verified = eSealStore.verifySeal();

    cards.push(
      <StoreCard key="eseal-dochash" title={t("eSeal.documentHash") || "Document Hash"} value={eSealStore.documentHash || "N/A"} color="neon" multiline description={t("eSeal.documentHashDesc") || "Cryptographic hash of the sealed document. Used for integrity verification and tamper detection."} />,
      <StoreCard key="eseal-confidence" title={t("eSeal.sealConfidence") || "Seal Confidence"} value={`${eSealStore.sealConfidence}%`} color={eSealStore.sealConfidence >= 80 ? "green" : eSealStore.sealConfidence >= 50 ? "yellow" : "red"} description={t("eSeal.sealConfidenceDesc") || "Confidence level of the eSeal (0-100%). Higher values indicate stronger cryptographic validation."} />,
      <StoreCard key="eseal-validator" title={t("eSeal.validatorRegion") || "Validator Region"} value={eSealStore.validatorRegion} color="gold" description={t("eSeal.validatorRegionDesc") || "Geographic region of the validator. Determines jurisdiction and legal validity."} />,
      <StoreCard key="eseal-ledgerheight" title={t("eSeal.anchorLedgerHeight") || "Anchor Ledger Height"} value={eSealStore.anchorLedgerHeight.toLocaleString()} color="neon" description={t("eSeal.anchorLedgerHeightDesc") || "Blockchain ledger height where the seal was anchored. Provides immutable timestamp proof."} />,
      <StoreCard key="eseal-timestamp" title={t("eSeal.sealTimestampISO") || "Seal Timestamp"} value={eSealStore.sealTimestampISO || "N/A"} color="blue" description={t("eSeal.sealTimestampISODesc") || "ISO 8601 timestamp when the seal was created. Used for audit trails and legal compliance."} />,
      <StoreCard key="eseal-verified" title={t("eSeal.verificationResult") || "Verification"} value={verified ? "✓ Valid" : eSealStore.sealSignature ? "✗ Invalid" : "Pending"} color={verified ? "green" : eSealStore.sealSignature ? "red" : "yellow"} description={t("eSeal.verificationResultDesc") || "Verification status: Valid = seal verified, Invalid = seal tampered or corrupted, Pending = verification in progress."} />
    );
  }

  // Certificate NFT cards
  if (activeRoute?.includes("certificatenft") || activeRoute?.includes("certificate-nft")) {
    const issuanceCostEUR = certificateNFTStore.getIssuanceCostEUR();
    const validityDays = certificateNFTStore.getValidityDays();
    const revocationProbability = certificateNFTStore.getRevocationProbability();

    cards.push(
      <StoreCard key="cert-level" title={t("certificateNFT.certificateLevel") || "Certificate Level"} value={certificateNFTStore.certificateLevel.toUpperCase()} color={certificateNFTStore.certificateLevel === "platinum" ? "gold" : certificateNFTStore.certificateLevel === "gold" ? "yellow" : certificateNFTStore.certificateLevel === "silver" ? "neon" : "blue"} description={t("certificateNFT.certificateLevelDesc") || "Certificate tier: Bronze (basic), Silver (standard), Gold (premium), Platinum (highest). Higher tiers have more validation."} />,
      <StoreCard key="cert-expiry" title={t("certificateNFT.expiryTimestampISO") || "Expiry"} value={certificateNFTStore.expiryTimestampISO || "N/A"} color="blue" description={t("certificateNFT.expiryTimestampISODesc") || "ISO 8601 timestamp when the certificate expires. After expiry, the certificate is no longer valid."} />,
      <StoreCard key="cert-cost" title={t("certificateNFT.issuanceCostEUR") || "Issuance Cost"} value={`€${issuanceCostEUR.toFixed(2)}`} color="gold" description={t("certificateNFT.issuanceCostEURDesc") || "Cost in EUR to issue the certificate. Higher tier certificates typically cost more."} />,
      <StoreCard key="cert-validity" title={t("certificateNFT.validityDays") || "Validity Days"} value={`${validityDays} days`} color="neon" description={t("certificateNFT.validityDaysDesc") || "Number of days the certificate remains valid. Longer validity periods provide more stability."} />,
      <StoreCard key="cert-revocation" title={t("certificateNFT.revocationProbability") || "Revocation Probability"} value={`${(revocationProbability * 100).toFixed(1)}%`} color={revocationProbability < 0.1 ? "green" : revocationProbability < 0.3 ? "yellow" : "red"} description={t("certificateNFT.revocationProbabilityDesc") || "Probability (0-100%) that the certificate will be revoked. Higher values indicate greater revocation risk."} />,
      <StoreCard key="cert-verifier" title={t("certificateNFT.verifierRegion") || "Verifier Region"} value={certificateNFTStore.verifierRegion} color="gold" description={t("certificateNFT.verifierRegionDesc") || "Geographic region of the certificate verifier. Determines jurisdiction and audit requirements."} />
    );

    if (certificateNFTStore.revocationRisk === 1.0) {
      cards.push(
        <div key="cert-revoked" className="border border-red-500/40 rounded p-3 bg-red-900/20 text-red-300 text-xs">
          ⚠️ {t("certificateNFT.revoked") || "Certificate has been revoked"}
        </div>
      );
    }
  }

  // EU Fabric cards
  if (activeRoute?.includes("eufabric") || activeRoute?.includes("eu-fabric")) {
    const interopPenalty = euFabricStore.getInteropPenalty();
    const borderFriction = euFabricStore.getBorderFriction();
    const fabricStatus = euFabricStore.getFabricStatus();

    cards.push(
      <StoreCard key="fabric-replag" title={t("euFabric.replicationLagMS") || "Replication Lag"} value={`${euFabricStore.replicationLagMS}ms`} color="neon" description={t("euFabric.replicationLagMSDesc") || "Time delay in milliseconds for data replication across EU nodes. Lower values indicate faster synchronization."} />,
      <StoreCard key="fabric-reconerrors" title={t("euFabric.reconciliationErrors") || "Reconciliation Errors"} value={euFabricStore.reconciliationErrors.toString()} color="red" description={t("euFabric.reconciliationErrorsDesc") || "Number of reconciliation errors detected. Higher counts indicate data consistency issues."} />,
      <StoreCard key="fabric-anomalies" title={t("euFabric.anomalyFlags") || "Anomaly Flags"} value={euFabricStore.anomalyFlags.toString()} color="yellow" description={t("euFabric.anomalyFlagsDesc") || "Number of anomaly flags raised. Indicates unusual patterns or potential issues in the fabric."} />,
      <StoreCard key="fabric-packets" title={t("euFabric.crossBorderPackets") || "Cross-Border Packets"} value={euFabricStore.crossBorderPackets.toLocaleString()} color="gold" description={t("euFabric.crossBorderPacketsDesc") || "Total number of data packets transmitted across EU borders. Monitors cross-border data flow."} />,
      <StoreCard key="fabric-escalation" title={t("euFabric.escalationLevel") || "Escalation Level"} value={euFabricStore.escalationLevel.toUpperCase()} color={euFabricStore.escalationLevel === "critical" ? "red" : euFabricStore.escalationLevel === "warning" ? "yellow" : "green"} description={t("euFabric.escalationLevelDesc") || "Escalation level: Normal (no issues), Warning (minor issues), Critical (severe issues requiring immediate attention)."} />,
      <StoreCard key="fabric-interoppenalty" title={t("euFabric.interopPenalty") || "Interop Penalty"} value={interopPenalty.toFixed(2)} color="orange" description={t("euFabric.interopPenaltyDesc") || "Penalty points for interoperability issues. Higher values indicate more compatibility problems."} />,
      <StoreCard key="fabric-borderfriction" title={t("euFabric.borderFriction") || "Border Friction"} value={borderFriction.toFixed(2)} color="purple" description={t("euFabric.borderFrictionDesc") || "Friction factor for cross-border operations. Higher values indicate more regulatory or technical barriers."} />,
      <StoreCard key="fabric-status" title={t("euFabric.fabricStatus") || "Fabric Status"} value={`${fabricStatus.toFixed(1)}%`} color={fabricStatus >= 80 ? "green" : fabricStatus >= 50 ? "yellow" : "red"} description={t("euFabric.fabricStatusDesc") || "Overall fabric health percentage (0-100%). Combines replication, errors, and interoperability metrics."} />,
      <StoreCard key="fabric-version" title={t("euFabric.interopVersion") || "Interop Version"} value={euFabricStore.interopVersion} color="blue" description={t("euFabric.interopVersionDesc") || "Interoperability protocol version. Ensures compatibility across EU member states."} />
    );
  }

  // Evidence Chain cards
  if (activeRoute?.includes("evidencechain") || activeRoute?.includes("evidence-chain") || activeRoute?.includes("evidence")) {
    const chainVolatility = evidenceChainStore.getChainVolatility();

    cards.push(
      <StoreCard key="evidence-chainlength" title={t("evidenceChain.chainLength") || "Chain Length"} value={evidenceChainStore.chainLength.toString()} color="gold" description={t("evidenceChain.chainLengthDesc") || "Number of evidence links in the chain. Longer chains provide more comprehensive audit trails."} />,
      <StoreCard key="evidence-integrity" title={t("evidenceChain.chainIntegrityScore") || "Chain Integrity Score"} value={`${evidenceChainStore.chainIntegrityScore.toFixed(1)}%`} color={evidenceChainStore.chainIntegrityScore >= 80 ? "green" : evidenceChainStore.chainIntegrityScore >= 50 ? "yellow" : "red"} description={t("evidenceChain.chainIntegrityScoreDesc") || "Chain integrity score (0-100%). Higher values indicate better cryptographic validation and tamper resistance."} />,
      <StoreCard key="evidence-admissibility" title={t("evidenceChain.admissibilityLevel") || "Admissibility Level"} value={evidenceChainStore.admissibilityLevel.toUpperCase()} color={evidenceChainStore.admissibilityLevel === "high" ? "green" : evidenceChainStore.admissibilityLevel === "medium" ? "yellow" : "red"} description={t("evidenceChain.admissibilityLevelDesc") || "Legal admissibility level: Low (weak evidence), Medium (moderate evidence), High (strong evidence admissible in court)."} />,
      <StoreCard key="evidence-volatility" title={t("evidenceChain.chainVolatility") || "Chain Volatility"} value={chainVolatility.toFixed(2)} color="orange" description={t("evidenceChain.chainVolatilityDesc") || "Volatility metric indicating chain stability. Higher values indicate more frequent changes or updates."} />,
      <StoreCard key="evidence-jitter" title={t("evidenceChain.chainJitterMS") || "Chain Jitter"} value={`${evidenceChainStore.chainJitterMS}ms`} color="purple" description={t("evidenceChain.chainJitterMSDesc") || "Time jitter in milliseconds. Measures timing inconsistencies in chain updates."} />,
      <StoreCard key="evidence-drift" title={t("evidenceChain.chainDriftMinutes") || "Chain Drift"} value={`${evidenceChainStore.chainDriftMinutes} min`} color="blue" description={t("evidenceChain.chainDriftMinutesDesc") || "Time drift in minutes. Indicates synchronization delays between chain nodes."} />
    );
  }

  // Tender Mesh cards
  if (activeRoute?.includes("tendermesh") || activeRoute?.includes("tender-mesh") || (activeRoute?.includes("tender") && activeRoute?.includes("mesh"))) {
    const conflictPenalty = tenderMeshStore.getConflictPenalty();
    const escalationLevel = tenderMeshStore.getEscalationLevel();

    cards.push(
      <StoreCard key="tender-transparency" title={t("tenderMesh.transparencyScore") || "Transparency Score"} value={`${tenderMeshStore.transparencyScore}%`} color={tenderMeshStore.transparencyScore >= 80 ? "green" : tenderMeshStore.transparencyScore >= 50 ? "yellow" : "red"} description={t("tenderMesh.transparencyScoreDesc") || "Transparency score (0-100%). Higher values indicate more open and accessible procurement processes."} />,
      <StoreCard key="tender-conflictpenalty" title={t("tenderMesh.conflictPenalty") || "Conflict Penalty"} value={conflictPenalty.toFixed(1)} color="orange" description={t("tenderMesh.conflictPenaltyDesc") || "Penalty points for conflicts of interest. Higher values indicate more detected conflicts in procurement."} />,
      <StoreCard key="tender-corruption" title={t("tenderMesh.corruptionSignals") || "Corruption Signals"} value={tenderMeshStore.corruptionSignals.toString()} color="red" description={t("tenderMesh.corruptionSignalsDesc") || "Number of corruption signals detected. Higher counts indicate greater risk of procurement fraud."} />,
      <StoreCard key="tender-geopolitical" title={t("tenderMesh.geopoliticalWeight") || "Geopolitical Weight"} value={tenderMeshStore.geopoliticalWeight.toFixed(1)} color="purple" description={t("tenderMesh.geopoliticalWeightDesc") || "Geopolitical risk weight (0-5). Higher values indicate greater geopolitical sensitivity or risk."} />,
      <StoreCard key="tender-latency" title={t("tenderMesh.meshLatencyMS") || "Mesh Latency"} value={`${tenderMeshStore.meshLatencyMS}ms`} color="neon" description={t("tenderMesh.meshLatencyMSDesc") || "Network latency in milliseconds for tender mesh operations. Lower values indicate faster communication."} />,
      <StoreCard key="tender-risk" title={t("tenderMesh.procurementRiskScore") || "Procurement Risk Score"} value={`${tenderMeshStore.procurementRiskScore.toFixed(1)}%`} color={tenderMeshStore.procurementRiskScore < 30 ? "green" : tenderMeshStore.procurementRiskScore < 60 ? "yellow" : "red"} description={t("tenderMesh.procurementRiskScoreDesc") || "Overall procurement risk score (0-100%). Combines transparency, conflicts, corruption, and geopolitical factors."} />,
      <StoreCard key="tender-escalation" title={t("tenderMesh.escalationLevel") || "Escalation Level"} value={escalationLevel.toUpperCase()} color={escalationLevel === "high" ? "red" : escalationLevel === "medium" ? "yellow" : "green"} description={t("tenderMesh.escalationLevelDesc") || "Escalation level: Low (no action needed), Medium (monitoring required), High (immediate investigation needed)."} />
    );
  }

  // Tribunal cards
  if (activeRoute?.includes("tribunal") || activeRoute?.includes("procurement-tribunal")) {
    const weightedSeverity = tribunalStore.getWeightedSeverity();

    cards.push(
      <StoreCard key="tribunal-decision" title={t("tribunal.tribunalDecision") || "Tribunal Decision"} value={tribunalStore.tribunalDecision.toUpperCase()} color={tribunalStore.tribunalDecision === "disqualification" ? "red" : tribunalStore.tribunalDecision === "fine" ? "yellow" : tribunalStore.tribunalDecision === "warning" ? "orange" : "blue"} description={t("tribunal.tribunalDecisionDesc") || "Tribunal decision: Pending (under review), Warning (minor violation), Fine (financial penalty), Disqualification (exclusion from procurement)."} />,
      <StoreCard key="tribunal-remediation" title={t("tribunal.remediationDemandEUR") || "Remediation Demand"} value={`€${tribunalStore.remediationDemandEUR.toLocaleString()}`} color="gold" description={t("tribunal.remediationDemandEURDesc") || "Financial amount (EUR) required for remediation. Must be paid to restore eligibility."} />,
      <StoreCard key="tribunal-appeal" title={t("tribunal.appealWindowDays") || "Appeal Window"} value={`${tribunalStore.appealWindowDays} days`} color="neon" description={t("tribunal.appealWindowDaysDesc") || "Number of days remaining to file an appeal. After this window closes, the decision becomes final."} />,
      <StoreCard key="tribunal-weighted" title={t("tribunal.weightedSeverity") || "Weighted Severity"} value={weightedSeverity.toFixed(1)} color={weightedSeverity >= 70 ? "red" : weightedSeverity >= 40 ? "yellow" : "green"} description={t("tribunal.weightedSeverityDesc") || "Weighted severity score (0-100). Combines evidence count, jurisdiction factors, and violation type."} />,
      <StoreCard key="tribunal-jurisdiction" title={t("tribunal.jurisdiction") || "Jurisdiction"} value={tribunalStore.jurisdiction} color="gold" description={t("tribunal.jurisdictionDesc") || "Legal jurisdiction handling the case. Determines applicable laws and appeal procedures."} />
    );
  }

  // ESG Passport Registry cards
  if (activeRoute?.includes("passport") || activeRoute?.includes("passport-registry") || activeRoute?.includes("esgpassport")) {
    const passportLevelDistribution = esgPassportRegistryStore.getPassportLevelDistribution();

    cards.push(
      <StoreCard key="passport-health" title={t("passport.registryHealth") || "Registry Health"} value={`${esgPassportRegistryStore.registryHealth.toFixed(1)}%`} color={esgPassportRegistryStore.registryHealth >= 80 ? "green" : esgPassportRegistryStore.registryHealth >= 50 ? "yellow" : "red"} description={t("passport.registryHealthDesc") || "Overall registry health percentage (0-100%). Combines update latency, revocation rate, and data consistency."} />,
      <StoreCard key="passport-revoked" title={t("passport.revokedCount") || "Revoked Count"} value={esgPassportRegistryStore.revokedCount.toString()} color="red" description={t("passport.revokedCountDesc") || "Number of passports revoked due to non-compliance or violations. Higher counts indicate more issues."} />,
      <StoreCard key="passport-latency" title={t("passport.updateLatencyMS") || "Update Latency"} value={`${esgPassportRegistryStore.updateLatencyMS}ms`} color="neon" description={t("passport.updateLatencyMSDesc") || "Time delay in milliseconds for registry updates. Lower values indicate faster synchronization."} />,
      <StoreCard key="passport-basic" title={t("passport.basic") || "Basic"} value={passportLevelDistribution.basic.toString()} color="blue" description={t("passport.basicDesc") || "Number of basic-level ESG passports. Basic passports have minimal validation requirements."} />,
      <StoreCard key="passport-enhanced" title={t("passport.enhanced") || "Enhanced"} value={passportLevelDistribution.enhanced.toString()} color="purple" description={t("passport.enhancedDesc") || "Number of enhanced-level ESG passports. Enhanced passports require additional validation and audits."} />,
      <StoreCard key="passport-sovereign" title={t("passport.sovereign") || "Sovereign"} value={passportLevelDistribution.sovereign.toString()} color="gold" description={t("passport.sovereignDesc") || "Number of sovereign-level ESG passports. Sovereign passports have the highest validation and cross-border recognition."} />
    );
  }

  // Subsidy Eligibility cards - check multiple possible route formats
  const routeLower = activeRoute?.toLowerCase() || "";
  if (routeLower.includes("subsidy") || routeLower === "subsidyeligibilityengine" || activePanel?.toLowerCase().includes("subsidy")) {
    // Use current computed values (don't call computeEligibility during render)
    const subsidyScore = subsidyEligibilityStore.getSubsidyEligibilityScore();
    const passportBonus = subsidyEligibilityStore.getPassportBonus();
    const corruptionPenalty = subsidyEligibilityStore.getCorruptionPenalty();
    const geoPenalty = subsidyEligibilityStore.getGeoPenalty();
    const tribunalPenalty = subsidyEligibilityStore.getTribunalPenalty();

    cards.push(
      <StoreCard key="subsidy-score" title={t("subsidy.subsidyEligibilityScore") || "Eligibility Score"} value={`${subsidyScore.toFixed(1)}%`} color={subsidyScore >= 70 ? "green" : subsidyScore >= 50 ? "yellow" : "red"} description={t("subsidy.subsidyEligibilityScoreDesc") || "Overall eligibility score (0-100). 70-100: Good, no escalation. 50-69: Medium, regional audit. 30-49: Low, EU audit. 0-29: Very low, prosecution possible."} />,
      <StoreCard key="subsidy-reduction" title={t("subsidy.grantReductionPercent") || "Grant Reduction"} value={`${subsidyEligibilityStore.grantReductionPercent.toFixed(1)}%`} color="orange" description={t("subsidy.grantReductionPercentDesc") || "Percentage reduction in grant amount (0-80%). Higher score = lower reduction."} />,
      <StoreCard key="subsidy-risk" title={t("subsidy.riskOfMisuseFlag") || "Risk of Misuse"} value={subsidyEligibilityStore.riskOfMisuseFlag ? "YES" : "NO"} color={subsidyEligibilityStore.riskOfMisuseFlag ? "red" : "green"} description={t("subsidy.riskOfMisuseFlagDesc") || "Flag indicating risk of subsidy misuse. YES = high risk, NO = low risk."} />,
      <StoreCard key="subsidy-escalation" title={t("subsidy.escalationPath") || "Escalation Path"} value={subsidyEligibilityStore.escalationPath.toUpperCase()} color={subsidyEligibilityStore.escalationPath === "prosecution" ? "red" : subsidyEligibilityStore.escalationPath === "euAudit" ? "orange" : subsidyEligibilityStore.escalationPath === "regionalAudit" ? "yellow" : "green"} description={t("subsidy.escalationPathDesc") || "Recommended escalation path based on eligibility score: none, regionalAudit, euAudit, or prosecution."} />,
      <StoreCard key="subsidy-corruption" title={t("subsidy.corruptionPenalty") || "Corruption Penalty"} value={`-${corruptionPenalty.toFixed(1)}`} color="red" description={t("subsidy.corruptionPenaltyDesc") || "Penalty points deducted from eligibility score based on corruption signals detected."} />,
      <StoreCard key="subsidy-geo" title={t("subsidy.geoPenalty") || "Geo Penalty"} value={`-${geoPenalty.toFixed(1)}`} color="red" description={t("subsidy.geoPenaltyDesc") || "Penalty points deducted from eligibility score based on geopolitical risk weight."} />,
      <StoreCard key="subsidy-passport" title={t("subsidy.passportBonus") || "Passport Bonus"} value={`+${passportBonus.toFixed(1)}`} color="green" description={t("subsidy.passportBonusDesc") || "Bonus points added to eligibility score based on ESG passport level (basic: +0, enhanced: +5, sovereign: +10)."} />
    );
  }

  // Subsidy Allocation (v2-v3) cards
  if (activeRoute?.includes("subsidyallocation") || activePanel?.toLowerCase().includes("subsidy allocation")) {
    try {
      const overExposed = subsidyAllocationStore.getOverExposedCountries();
      const highFraudRisk = subsidyAllocationStore.getHighFraudRiskCountries();
      
      cards.push(
        <StoreCard key="subsidyalloc-total" title={t("subsidyAllocation.totalAllocated") || "Total Allocated"} value={`€${(subsidyAllocationStore.getTotalAllocated() / 1000000).toFixed(1)}M`} color="neon" description={t("subsidyAllocation.totalAllocatedDesc") || "Total amount of subsidies allocated across all countries. Monitors overall EU subsidy distribution."} />,
        <StoreCard key="subsidyalloc-remaining" title={t("subsidyAllocation.remainingBudget") || "Remaining Budget"} value={`€${(subsidyAllocationStore.getRemainingBudget() / 1000000).toFixed(1)}M`} color="green" description={t("subsidyAllocation.remainingBudgetDesc") || "Remaining budget available for allocation. Lower values indicate budget constraints."} />,
        <StoreCard key="subsidyalloc-overexposed" title={t("subsidyAllocation.overExposed") || "Over-Exposed"} value={overExposed.length.toString()} color={overExposed.length > 0 ? "red" : "green"} description={t("subsidyAllocation.overExposedDesc") || "Number of countries exceeding their plafon limits. Over-exposure may trigger EU audits."} />,
        <StoreCard key="subsidyalloc-highfraud" title={t("subsidyAllocation.highFraudRisk") || "High Fraud Risk"} value={highFraudRisk.length.toString()} color={highFraudRisk.length > 0 ? "red" : "green"} description={t("subsidyAllocation.highFraudRiskDesc") || "Number of countries with high fraud risk scores. Requires enhanced monitoring and validation."} />,
        <StoreCard key="subsidyalloc-totalplafon" title={t("subsidyAllocation.totalEUPlafon") || "Total EU Plafon"} value={`€${(subsidyAllocationStore.totalEUPlafon / 1000000).toFixed(0)}M`} color="gold" description={t("subsidyAllocation.totalEUPlafonDesc") || "Total EU plafon (ceiling) for all member states. Represents the maximum allowable subsidy pool."} />
      );
    } catch (error) {
      console.error("Error rendering Subsidy Allocation cards:", error);
    }
  }

  // Sovereign Reconciliation (v3) cards
  if (activeRoute?.includes("sovereignreconciliation") || activePanel?.toLowerCase().includes("sovereign reconciliation")) {
    try {
      const summary = sovereignReconciliationStore.getReconciliationSummary();
      
      cards.push(
        <StoreCard key="recon-total" title={t("sovereignReconciliation.totalReconciliations") || "Total Reconciliations"} value={summary.totalReconciliations.toString()} color="neon" description={t("sovereignReconciliation.totalReconciliationsDesc") || "Total number of reconciliation transactions processed. Tracks cross-sovereign financial settlements."} />,
        <StoreCard key="recon-totalamount" title={t("sovereignReconciliation.totalAmount") || "Total Amount"} value={summary.totalAmount.toLocaleString()} color="gold" description={t("sovereignReconciliation.totalAmountDesc") || "Total financial amount reconciled across all transactions. Represents the volume of cross-border settlements."} />,
        <StoreCard key="recon-countries" title={t("sovereignReconciliation.countries") || "Countries"} value={Object.keys(summary.byCountry).length.toString()} color="green" description={t("sovereignReconciliation.countriesDesc") || "Number of countries involved in reconciliations. Indicates the geographic scope of settlements."} />,
        <StoreCard key="recon-audit" title={t("sovereignReconciliation.auditEntries") || "Audit Entries"} value={sovereignReconciliationStore.auditTrail.length.toString()} color="blue" description={t("sovereignReconciliation.auditEntriesDesc") || "Number of audit trail entries. Provides immutable record of all reconciliation activities for compliance."} />
      );
    } catch (error) {
      console.error("Error rendering Sovereign Reconciliation cards:", error);
    }
  }

  // Beneficial Ownership Graph cards
  const beneficialRouteLower = activeRoute?.toLowerCase() || "";
  if (beneficialRouteLower.includes("beneficial") || beneficialRouteLower.includes("ownership") || activePanel?.toLowerCase().includes("beneficial")) {
    // Use state values directly - they're already calculated and updated
    const conflictScore = beneficialGraphStore.conflictScore;
    const oligarchClusterScore = beneficialGraphStore.oligarchClusterScore;
    const integrityRisk = beneficialGraphStore.integrityRisk;
    const graphDepth = beneficialGraphStore.graphDepth;

    cards.push(
      <StoreCard key="beneficial-conflict" title={t("graph.conflictScore") || "Conflict Score"} value={`${conflictScore.toFixed(1)}%`} color={conflictScore >= 70 ? "red" : conflictScore >= 50 ? "yellow" : "green"} description={t("graph.conflictScoreDesc") || "Conflict score calculated from nepotism flags, tender collisions, and graph depth. Higher values indicate more conflicts."} />,
      <StoreCard key="beneficial-oligarch" title={t("graph.oligarchClusterScore") || "Oligarch Cluster Score"} value={`${oligarchClusterScore.toFixed(1)}%`} color={oligarchClusterScore >= 70 ? "red" : oligarchClusterScore >= 50 ? "orange" : "yellow"} description={t("graph.oligarchClusterScoreDesc") || "Oligarch cluster score based on number of edges and average risk score. Indicates concentration of power."} />,
      <StoreCard key="beneficial-integrity" title={t("graph.integrityRisk") || "Integrity Risk"} value={`${integrityRisk.toFixed(1)}%`} color={integrityRisk >= 70 ? "red" : integrityRisk >= 50 ? "yellow" : "green"} description={t("graph.integrityRiskDesc") || "Overall integrity risk combining conflict score and oligarch cluster score. Higher values indicate greater governance risk."} />,
      <StoreCard key="beneficial-nepotism" title={t("graph.nepotismFlags") || "Nepotism Flags"} value={beneficialGraphStore.nepotismFlags.toString()} color="red" description={t("graph.nepotismFlagsDesc") || "Number of nepotism flags detected based on family relationships in the ownership graph."} />,
      <StoreCard key="beneficial-tender" title={t("graph.tenderCollisionCount") || "Tender Collisions"} value={beneficialGraphStore.tenderCollisionCount.toString()} color="yellow" description={t("graph.tenderCollisionCountDesc") || "Number of tender collisions manually flagged. Indicates potential conflicts of interest in procurement."} />,
      <StoreCard key="beneficial-depth" title={t("graph.graphDepth") || "Graph Depth"} value={graphDepth.toString()} color="neon" description={t("graph.graphDepthDesc") || "Maximum number of hops between nodes in the ownership graph. Deeper graphs may indicate complex ownership structures."} />
    );
  }

  // Judicial Trigger Engine cards
  const judicialRouteLower = activeRoute?.toLowerCase() || "";
  if (judicialRouteLower.includes("judicial") || judicialRouteLower.includes("trigger") || activePanel?.toLowerCase().includes("judicial")) {
    const judicialAction = judicialTriggerStore.judicialAction;
    const triggerSeverity = judicialTriggerStore.triggerSeverity;
    const recommendedAuditorTier = judicialTriggerStore.recommendedAuditorTier;
    const evidenceAdmissibility = judicialTriggerStore.evidenceAdmissibility;
    const tribunalDecision = judicialTriggerStore.tribunalDecision;
    const geopoliticalWeight = judicialTriggerStore.geopoliticalWeight;

    cards.push(
      <StoreCard key="judicial-action" title={t("judicial.judicialAction") || "Judicial Action"} value={judicialAction} color={judicialAction === "EU-Prosecution" ? "red" : judicialAction === "NationalInvestigation" ? "orange" : judicialAction === "RegionalInvestigation" ? "yellow" : "green"} description={t("judicial.judicialActionDesc") || "Recommended judicial action based on trigger severity: MonitorOnly, RegionalInvestigation, NationalInvestigation, or EU-Prosecution."} />,
      <StoreCard key="judicial-severity" title={t("judicial.triggerSeverity") || "Trigger Severity"} value={`${triggerSeverity.toFixed(1)}%`} color={triggerSeverity >= 75 ? "red" : triggerSeverity >= 60 ? "orange" : triggerSeverity >= 40 ? "yellow" : "green"} description={t("judicial.triggerSeverityDesc") || "Overall trigger severity (0-100) calculated from conflict score, oligarch cluster, procurement risk, integrity risk, and geopolitical weight."} />,
      <StoreCard key="judicial-auditor" title={t("judicial.recommendedAuditorTier") || "Auditor Tier"} value={`Tier ${recommendedAuditorTier}`} color="gold" description={t("judicial.recommendedAuditorTierDesc") || "Recommended auditor tier (1-3) based on trigger severity. Tier 1 = basic, Tier 2 = intermediate, Tier 3 = expert."} />,
      <StoreCard key="judicial-evidence" title={t("judicial.evidenceAdmissibility") || "Evidence Admissibility"} value={evidenceAdmissibility.toUpperCase()} color={evidenceAdmissibility === "high" ? "green" : evidenceAdmissibility === "medium" ? "yellow" : "red"} description={t("judicial.evidenceAdmissibilityDesc") || "Evidence admissibility level: low, medium, or high. Low admissibility reduces trigger severity by -5 points."} />,
      <StoreCard key="judicial-tribunal" title={t("judicial.tribunalDecision") || "Tribunal Decision"} value={tribunalDecision.toUpperCase()} color={tribunalDecision === "disqualification" ? "red" : tribunalDecision === "fine" ? "orange" : tribunalDecision === "warning" ? "yellow" : "blue"} description={t("judicial.tribunalDecisionDesc") || "Current tribunal decision status: pending, warning, fine, or disqualification. Disqualification adds +10 to trigger severity."} />,
      <StoreCard key="judicial-geo" title={t("judicial.geopoliticalWeight") || "Geopolitical Weight"} value={`${geopoliticalWeight.toFixed(1)}/5.0`} color="neon" description={t("judicial.geopoliticalWeightDesc") || "Geopolitical risk weight (0-5) affecting trigger severity calculation. Higher values indicate greater geopolitical risk."} />
    );
  }

  // Satellite Overlay Risk cards
  const satelliteRouteLower = activeRoute?.toLowerCase() || "";
  if (satelliteRouteLower.includes("satelliteoverlay") || satelliteRouteLower.includes("satellite-overlay") || activePanel?.toLowerCase().includes("satellite overlay")) {
    const overlayRiskIndex = satelliteOverlayStore.overlayRiskIndex;
    const progressMismatch = satelliteOverlayStore.progressMismatch;
    const landUseMismatch = satelliteOverlayStore.landUseMismatch;
    const procurementAnomaly = satelliteOverlayStore.procurementAnomaly;
    const satelliteDriftScore = satelliteOverlayStore.satelliteDriftScore;
    const crossBorderSuspicion = satelliteOverlayStore.crossBorderSuspicion;
    const ghostSiteFlag = satelliteOverlayStore.ghostSiteFlag;

    // Ghost Site Alert Badge
    if (ghostSiteFlag) {
      cards.push(
        <div key="satellite-ghost-badge" className="border border-red-500/35 bg-ff0033/20 rounded-lg p-3" style={{ backgroundColor: "#ff0033", border: "1px solid rgba(255,0,0,0.35)" }}>
          <div className="text-red-100 font-orbitron font-bold text-center">
            ⚠️ GHOST CONSTRUCTION ALERT
          </div>
        </div>
      );
    }

    cards.push(
      <StoreCard key="satellite-risk" title={t("sat.overlay.overlayRiskIndex") || "Overlay Risk Index"} value={`${overlayRiskIndex.toFixed(1)}%`} color={overlayRiskIndex >= 75 ? "red" : overlayRiskIndex >= 50 ? "orange" : overlayRiskIndex >= 25 ? "yellow" : "green"} description={t("sat.overlay.overlayRiskIndexDesc") || "Overall risk index combining progress mismatch, land use, procurement anomalies, and satellite drift. Higher values indicate greater risk."} />,
      <StoreCard key="satellite-progress" title={t("sat.overlay.progressMismatch") || "Progress Mismatch"} value={`${progressMismatch.toFixed(1)}%`} color={progressMismatch >= 50 ? "red" : progressMismatch >= 25 ? "yellow" : "green"} description={t("sat.overlay.progressMismatchDesc") || "Difference between construction progress and budget disbursement. Large mismatches may indicate fraud or project delays."} />,
      <StoreCard key="satellite-landuse" title={t("sat.overlay.landUseMismatch") || "Land Use Mismatch"} value={`${landUseMismatch.toFixed(1)}%`} color={landUseMismatch >= 70 ? "red" : landUseMismatch >= 40 ? "yellow" : "green"} description={t("sat.overlay.landUseMismatchDesc") || "Discrepancy between planned and actual land use. High values suggest unauthorized construction or zoning violations."} />,
      <StoreCard key="satellite-procurement" title={t("sat.overlay.procurementAnomaly") || "Procurement Anomaly"} value={`${procurementAnomaly.toFixed(1)}%`} color={procurementAnomaly >= 70 ? "red" : procurementAnomaly >= 40 ? "yellow" : "green"} description={t("sat.overlay.procurementAnomalyDesc") || "Unusual patterns in procurement processes. High values may indicate corruption or favoritism in tender processes."} />,
      <StoreCard key="satellite-drift" title={t("sat.overlay.satelliteDriftScore") || "Satellite Drift Score"} value={satelliteDriftScore.toFixed(1)} color={satelliteDriftScore >= 70 ? "red" : satelliteDriftScore >= 40 ? "yellow" : "green"} description={t("sat.overlay.satelliteDriftScoreDesc") || "Deviation between satellite imagery and reported project status. High scores suggest misreporting or ghost construction sites."} />,
      <StoreCard key="satellite-crossborder" title={t("sat.overlay.crossBorderSuspicion") || "Cross-Border Suspicion"} value={`${crossBorderSuspicion.toFixed(1)}/5.0`} color={crossBorderSuspicion >= 4 ? "red" : crossBorderSuspicion >= 2 ? "yellow" : "green"} description={t("sat.overlay.crossBorderSuspicionDesc") || "Suspicion level for cross-border fraud or money laundering. Scale: 0 (none) to 5 (critical)."} />,
      <StoreCard key="satellite-ghost" title={t("sat.overlay.ghostSiteFlag") || "Ghost Site Flag"} value={ghostSiteFlag ? "TRUE" : "FALSE"} color={ghostSiteFlag ? "red" : "green"} description={t("sat.overlay.ghostSiteFlagDesc") || "Alert: Construction progress <10% but budget disbursement >30%. Indicates potential ghost construction site fraud."} />
    );
  }

  // Anti-Fraud Interoperability Layer cards
  const antifraudRouteLower = activeRoute?.toLowerCase() || "";
  if (antifraudRouteLower.includes("antifraud") || antifraudRouteLower.includes("anti-fraud") || activePanel?.toLowerCase().includes("anti-fraud")) {
    const riskScore = antiFraudStore.riskScore;
    const riskBand = antiFraudStore.riskBand;
    const peppolStatus = antiFraudStore.peppolStatus;
    const peppolSealValid = antiFraudStore.peppolSealValid;
    const uboHints = antiFraudStore.uboHints;
    const uboEdgesCount = antiFraudStore.uboEdgesCount;

    cards.push(
      <StoreCard key="antifraud-risk" title={t("antifraud.riskScoring") || "Risk Score"} value={`${riskScore}%`} color={riskBand === "CRITICAL" ? "red" : riskBand === "HIGH" ? "orange" : riskBand === "MEDIUM" ? "yellow" : "green"} description={t("antifraud.riskScoringDesc") || "Transaction risk score (0-100%). Calculated from amount, country, channel, and metadata patterns. Higher values indicate greater fraud risk."} />,
      <StoreCard key="antifraud-band" title={t("antifraud.riskBand") || "Risk Band"} value={riskBand} color={riskBand === "CRITICAL" ? "red" : riskBand === "HIGH" ? "orange" : riskBand === "MEDIUM" ? "yellow" : "green"} description={t("antifraud.riskBandDesc") || "Risk band classification: LOW (safe), MEDIUM (monitor), HIGH (investigate), CRITICAL (block immediately)."} />,
      <StoreCard key="antifraud-peppol" title={t("antifraud.peppolVerify") || "PEPPOL Status"} value={peppolStatus} color={peppolStatus === "VALID" ? "green" : "red"} description={t("antifraud.peppolVerifyDesc") || "PEPPOL (Pan-European Public Procurement On-Line) verification status. VALID = compliant with EU procurement standards."} />,
      <StoreCard key="antifraud-seal" title={t("antifraud.sealValid") || "Seal Valid"} value={peppolSealValid ? "YES" : "NO"} color={peppolSealValid ? "green" : "red"} description={t("antifraud.sealValidDesc") || "eSeal cryptographic validation. YES = document integrity verified, NO = seal invalid or missing."} />,
      <StoreCard key="antifraud-ubo" title={t("antifraud.uboHints") || "UBO Hints"} value={uboHints.length > 0 ? `${uboHints.length} hint(s)` : "None"} color={uboHints.length > 0 ? "yellow" : "green"} description={t("antifraud.uboHintsDesc") || "Ultimate Beneficial Ownership (UBO) conflict hints detected. Examples: PEP_link_detected, circular_control, family_link."} />,
      <StoreCard key="antifraud-edges" title={t("antifraud.uboEdges") || "UBO Edges"} value={uboEdgesCount.toString()} color="neon" description={t("antifraud.uboEdgesDesc") || "Number of ownership graph edges analyzed. More edges may indicate complex ownership structures requiring deeper investigation."} />
    );
  }

  // Mini ESG cards
  if (activeRoute?.includes("miniesg") || activePanel === "MiniESGPanel") {
    cards.push(
      <StoreCard key="miniesg-total" title={t("miniESG.totalScore") || "Total Score"} value={`${miniESGStore.score.total} / 30`} color={miniESGStore.score.level === "red" ? "red" : miniESGStore.score.level === "yellow" ? "yellow" : "green"} description={t("miniESG.scoreDesc") || "Overall ESG score (0-30 points). 0-10: Basic level, 11-20: Intermediate level, 21-30: Good level."} />,
      <StoreCard key="miniesg-env" title={t("miniESG.score.environment") || "Environment"} value={`${miniESGStore.score.environment} / 12`} color="green" description={t("miniESG.environmentScoreDesc") || "Environment block score (0-12 points). Based on energy consumption, waste management, eco-materials, CO₂ data, and environmental goals."} />,
      <StoreCard key="miniesg-social" title={t("miniESG.score.social") || "Social"} value={`${miniESGStore.score.social} / 10`} color="blue" description={t("miniESG.socialScoreDesc") || "Social block score (0-10 points). Based on employee count, workplace safety, training programs, equality policies, and social projects."} />,
      <StoreCard key="miniesg-gov" title={t("miniESG.score.governance") || "Governance"} value={`${miniESGStore.score.governance} / 8`} color="purple" description={t("miniESG.governanceScoreDesc") || "Governance block score (0-8 points). Based on compliance policy, ethics code, data security, and sustainability reporting."} />
    );
  }

  } catch (error) {
    console.error("Error in ModuleStoreCards:", error);
    return null;
  }

  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-3 mt-4">
      {cards.filter(Boolean)}
    </div>
  );
}

/**
 * Store data card component
 */
function StoreCard({ title, value, color, multiline, description }: { title: string; value: string | number; color: "red" | "yellow" | "orange" | "purple" | "green" | "blue" | "gold" | "neon"; multiline?: boolean; description?: string }) {
  
  const colorClassesDark = {
    red: "text-red-400 border-red-500/40",
    yellow: "text-yellow-400 border-yellow-500/40",
    orange: "text-orange-400 border-orange-500/40",
    purple: "text-purple-400 border-purple-500/40",
    green: "text-green-400 border-green-500/40",
    blue: "text-blue-400 border-blue-500/40",
    gold: "text-evat-gold border-evat-gold/40",
    neon: "text-evat-neon border-evat-neon/40",
  };

  const colorClassesLight = {
    red: "text-red-600 border-red-600/60",
    yellow: "text-yellow-600 border-yellow-600/60",
    orange: "text-orange-600 border-orange-600/60",
    purple: "text-purple-600 border-purple-600/60",
    green: "text-green-600 border-green-600/60",
    blue: "text-blue-600 border-blue-600/60",
    gold: "text-amber-700 border-amber-700/60",
    neon: "text-blue-600 border-blue-600/60",
  };

  const colorClasses = colorClassesDark; // Always dark mode

  return (
    <div
      className={`border ${colorClasses[color]} rounded-lg p-3 bg-[#0b0b0b] hover:border-evat-neon/60 transition-colors font-orbitron`}
      style={{ borderRadius: "8px", padding: "10px", border: "1px solid rgba(0,255,153,0.35)" }}
    >
      <div className="text-xs mb-1 text-neutral-400">{title}</div>
      <div className={`text-sm font-orbitron ${multiline ? "break-words" : ""} ${colorClasses[color].split(" ")[0]}`}>
        {value}
      </div>
      {description && (
        <div className="mt-2 text-xs text-neutral-500 leading-relaxed border-t border-neutral-800 pt-2">
          {description}
        </div>
      )}
    </div>
  );
}

